import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TiTick, TiTimes } from 'react-icons/ti';
import axios from 'axios';

/**
 * Página de resultado del pago CLA (éxito / rechazo).
 *
 * El `?o=` es un token opaco firmado y con vencimiento que emite el backend al
 * volver de Webpay — antes era el id de la compra, que se podía enumerar para
 * leer los datos de otros inscritos (hallazgo H02 del ethical hacking). Si el
 * token venció o no valida, la página sigue funcionando: muestra la confirmación
 * sin el detalle en vez de quedarse en blanco.
 */

const PROGRAMA = 'Programa IA · Caja Los Andes';
const LOGO_DL = `${process.env.PUBLIC_URL}/logos/desafio-latam-color.png`;
const LOGO_CLA = `${process.env.PUBLIC_URL}/logos/caja-los-andes.svg`;

type Result = {
  status: string;
  name: string;
  amount: number;
  program: string;
  date: string;
};

const clp = (n?: number) =>
  typeof n === 'number' ? '$' + n.toLocaleString('es-CL') : '';

export const ClaResultPage = ({ success = false }: { success?: boolean }) => {
  const [params] = useSearchParams();
  const token = params.get('o');
  const [data, setData] = useState<Result | null>(null);
  const [sinDetalle, setSinDetalle] = useState(!token);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${process.env.REACT_APP_API_BACKEND}/cla/result/${encodeURIComponent(token)}`)
      .then((r) => setData(r.data))
      .catch(() => setSinDetalle(true)); // token vencido o inválido
  }, [token]);

  const firstName = data?.name?.split(' ')[0];

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#E8F1FF] to-[#F5F7FA] px-4 py-10">
      <div className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-xl">
        {/* Logos */}
        <div className="mb-8 flex items-center justify-center gap-4">
          <img src={LOGO_DL} alt="Desafío Latam" className="h-7 w-auto" />
          <span className="h-6 w-px bg-gray-200" />
          <img src={LOGO_CLA} alt="Caja Los Andes" className="h-8 w-auto" />
        </div>

        {success ? (
          <>
            <span className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[#00A676]/10 ring-4 ring-[#00A676]/15">
              <TiTick className="h-12 w-12 text-[#00A676]" />
            </span>
            <h1 className="mt-6 text-2xl font-extrabold text-[#0B1F3A]">
              {firstName ? `¡Gracias, ${firstName}!` : '¡Pago exitoso!'}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-gray-500">
              Quedaste inscrito(a) en el programa{' '}
              <strong className="text-[#0B1F3A]">{data?.program || PROGRAMA}</strong>.
              {sinDetalle ? (
                <> Te enviamos el detalle de tu inscripción por correo.</>
              ) : (
                <> Mantente atento(a) a tu correo: pronto te enviaremos tus credenciales y datos de acceso.</>
              )}{' '}
              ¡Bienvenido(a)! 🎉
            </p>

            {data && (
              <dl className="mt-6 space-y-2 rounded-2xl bg-[#F5F7FA] p-5 text-left text-sm">
                {data.name && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Alumno(a)</dt>
                    <dd className="font-medium text-[#0B1F3A]">{data.name}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Copago pagado</dt>
                  <dd className="font-bold text-[#00A676]">{clp(data.amount)}</dd>
                </div>
                {data.date && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Fecha</dt>
                    <dd className="font-medium text-[#0B1F3A]">{data.date}</dd>
                  </div>
                )}
              </dl>
            )}
          </>
        ) : (
          <>
            <span className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[#FFC857]/15 ring-4 ring-[#FFC857]/20">
              <TiTimes className="h-12 w-12 text-[#E0A100]" />
            </span>
            <h1 className="mt-6 text-2xl font-extrabold text-[#0B1F3A]">El pago no se completó</h1>
            <p className="mt-3 text-sm leading-relaxed text-gray-500">
              No se realizó ningún cobro. Puedes volver a la página del programa y
              reintentar tu inscripción cuando quieras.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
